import axios, { AxiosError, AxiosResponse } from 'axios';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

axiosInstance.interceptors.request.use((request) => {
  const token = localStorage.getItem('token');

  if (!token) {
    return request;
  }

  request.headers['Authorization'] = `Bearer ${token}`;

  return request;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      console.error('Response error:', error.response.status, error.response.data);

      const data = error.response.data as { code?: string };

      if (error.response.status === 401 && data.code && data.code === 'token_not_valid') {
        localStorage.removeItem('token');
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Axios setup error:', error.message);
    }

    return Promise.reject(error);
  },
);

type Progress = {
  percent: number;
  totalAnswers: number;
  totalQuestions: number;
};

export type StoryQuestion = {
  id: string;
  content: string | null;
  category: number;
  story: string;
  answer: string | null;

  // Binary
  audioAnswer: string | null;
};

export type StoryQuestionsCategory = {
  id: string;
  name: string;
  story: string;
  questions: StoryQuestion[];
  progress: Progress;
  description?: string | null;
};

export type StoryMetadata = {
  locale?: 'en' | 'ua';
  fontSize?: string;
  shareLinkMessage?: string;
  [key: string]: string | undefined;
};

export type Story = {
  id: string;
  status: string;
  metadata: StoryMetadata;
  user: number;
  questionsCategories: StoryQuestionsCategory[];
  name: string | null;
  progress: Progress;
  template: DefaultStory | null;
  isPersonal: boolean;
};

export type StoryGuest = {
  id: string;
  metadata: StoryMetadata;
  author: string;
  questionsCategories: StoryQuestionsCategory[];
  name: string | null;
};

export type APIGetStoriesParams = {
  isPersonal?: boolean;
};

export const apiGetStories = (params?: APIGetStoriesParams) =>
  axiosInstance.get<Story[]>('stories', {
    params,
  });

export const apiGetStoryById = (id: string) => axiosInstance.get<Story>(`stories/${id}/`);

export type APIPutStoryPayload = Partial<{
  metadata: StoryMetadata;
  name: string;
}>;

export const apiPutStoryById = (id: string, payload: APIPutStoryPayload) =>
  axiosInstance.put<Story, AxiosResponse<Story>, APIPutStoryPayload>(
    `stories/${id}/`,
    payload,
  );

export const apiDeleteStoryById = (id: string) => axiosInstance.delete(`stories/${id}/`);

export type APIPostStoriesPayload = {
  defaultStoryId?: string;
  isPersonal?: boolean;
};

export const apiPostStories = (payload: APIPostStoriesPayload) =>
  axiosInstance.post<Story>('stories', payload);

export type DefaultStory = {
  id: string;
  name: string;
  displayName: string;
  description: string;
};

export type APIGetDefaultStoriesParams = {
  locale?: 'en' | 'ua';
};

export const apiGetDefaultStories = (params?: APIGetDefaultStoriesParams) =>
  axiosInstance.get<DefaultStory[]>('default-stories', { params });

export type APIPutStoryQuestionsCategoryPayload = Partial<{
  name: string | null;
  storyId: string;
}>;

export const apiPutStoryQuestionsCategoryById = (
  id: string,
  payload: APIPutStoryQuestionsCategoryPayload,
) =>
  axiosInstance.put<
    StoryQuestionsCategory,
    AxiosResponse<StoryQuestionsCategory>,
    APIPutStoryQuestionsCategoryPayload
  >(`story-questions-categories/${id}/`, payload);

export type APIPostStoryQuestionsCategoryPayload = {
  name?: string | null;
  storyId: string;
};

export const apiPostStoryQuestionsCategoryById = (
  payload: APIPostStoryQuestionsCategoryPayload,
) =>
  axiosInstance.post<
    StoryQuestionsCategory,
    AxiosResponse<StoryQuestionsCategory>,
    APIPostStoryQuestionsCategoryPayload
  >(`story-questions-categories`, payload);

export const apiDeleteStoryQuestionsCategoryById = (id: string) =>
  axiosInstance.delete<StoryQuestionsCategory>(`story-questions-categories/${id}/`);

export type APIPutStoryQuestionPayload = Partial<{
  content: string | null;
  category: number;
  answer: string | null;
  audioAnswer: string | null;
}>;

export const apiPutStoryQuestionById = (
  id: string,
  payload: APIPutStoryQuestionPayload,
) =>
  axiosInstance.put<
    StoryQuestion,
    AxiosResponse<StoryQuestion>,
    APIPutStoryQuestionPayload
  >(`story-questions/${id}/`, payload);

export type APIPostStoryQuestionPayload = {
  categoryId: string;
  content?: string;
};

export const apiPostStoryQuestion = (payload: APIPostStoryQuestionPayload) =>
  axiosInstance.post<
    StoryQuestion,
    AxiosResponse<StoryQuestion>,
    APIPostStoryQuestionPayload
  >(`story-questions`, payload);

export const apiDeleteStoryQuestionById = (id: string) =>
  axiosInstance.delete<StoryQuestion>(`story-questions/${id}/`);

export type APIReorderStoryQuestionPayload = {
  newIndex: number;
  categoryId: string;
};

export const apiReorderStoryQuestion = (
  id: string,
  payload: APIReorderStoryQuestionPayload,
) =>
  axiosInstance.post<
    { newRank: string },
    AxiosResponse<{ newRank: string }>,
    APIReorderStoryQuestionPayload
  >(`story-questions/${id}/reorder/`, payload);

export type Me = {
  id: number;
  email: string;
};

export const apiGetMe = () => axiosInstance.get<Me>('me');

export type AuthToken = {
  access: string;
  refresh: string;
};

export type APIPostAuthLoginPayload = {
  email: string;
  password: string;
};

export const apiPostAuthLogin = (payload: APIPostAuthLoginPayload) =>
  axiosInstance.post<AuthToken, AxiosResponse<AuthToken>, APIPostAuthLoginPayload>(
    'auth/dj-rest-auth/login',
    payload,
  );

// export const apiPutStoryAnswerById = (id: string, payload: APIPutStoryAnswerPayload) =>
//   axiosInstance.put<
//     StoryQuestionAnswer,
//     StoryQuestionAnswer,
//     Partial<StoryQuestionAnswer>
//   >(`story-answers/${id}/`, payload);
